import React, { useEffect, useRef } from "react";
import { VideoTypeEnum } from "../../types/index";
import { FileSelector } from "./FileSelector";
import { RecordList } from "./RecordList";
import { UploadItem, useBatchUpload } from "./useBatchUpload";

interface BatchUploadModalProps {
    onHide: () => void;
    onComplete: (result?: { success: boolean; message: string }) => void;
}

export const BatchUploadModal: React.FC<BatchUploadModalProps> = ({
    onHide,
    onComplete
}) => {
    const {
        setStep,
        batchName,
        setBatchName,
        selectedVideoFiles,
        recordGroups,
        step,
        handleFileSelect,
        removeFile,
        clearFiles,
        groupVideos,
        resetState,
        createRecordsAndUpload,
        setGroupReupload,
        uploadQueue,
        error,
        clearError,
        isCreatingRecords,
        isUploading
    } = useBatchUpload(onHide, onComplete);

    const resetStateRef = useRef(resetState);
    useEffect(() => { resetStateRef.current = resetState; }, [resetState]);

    useEffect(() => {
        resetStateRef.current();
    }, []);

    const hasVideosToUpload = uploadQueue.length > 0;

    const handleNext = async () => {
        clearError();
        if (step === "select") {
            await groupVideos();
        } else if (step === "review") {
            createRecordsAndUpload();
        } else if (step === "upload") {
            onHide();
        }
    };

    const handleBack = () => {
        if (step === "review") {
            setStep("select");
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {step === "select" ? "Batch Upload Videos" :
                                step === "review" ? "Review Records" :
                                    "Uploading Videos"}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onHide}
                            disabled={isCreatingRecords || isUploading}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger mb-3">
                                {error}
                            </div>
                        )}
                        {step === "select" && (
                            <>
                                <div className="mb-3">
                                    <label htmlFor="batchName" className="form-label">
                                        Batch Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="batchName"
                                        value={batchName}
                                        onChange={(e) => setBatchName(e.target.value)}
                                        placeholder="Enter batch name"
                                        required
                                    />
                                    <div className="form-text">
                                        This name will be used as a prefix for all created records.
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Select Videos</label>
                                    <FileSelector onFilesSelected={handleFileSelect} />
                                    <small className="text-muted d-block mt-1">
                                        Large files (&gt;50Mo) will be automatically uploaded in chunks for better reliability
                                    </small>
                                    <small className="text-muted d-block mt-1">
                                        MP4 files create PHOTO records; .360 files create PANORAMA records. Files must match the campaign type.
                                    </small>
                                </div>
                                {selectedVideoFiles.length > 0 && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <p className="mb-0">Selected {selectedVideoFiles.length} files:</p>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={clearFiles}
                                                title="Clear all files"
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        <ul className="list-group">
                                            {selectedVideoFiles.slice(0, 5).map((file: { file: File; type: VideoTypeEnum; isCorrupted?: boolean; corruptionError?: string }, index: number) => (
                                                <li key={index} className={`list-group-item ${file.isCorrupted ? 'list-group-item-danger' : ''}`}>
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div className="flex-grow-1" style={{ maxWidth: '70%' }}>
                                                            <div className="text-truncate">
                                                                {file.file.name}
                                                            </div>
                                                            {file.isCorrupted && (
                                                                <div className="text-danger small mt-1">
                                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                                    {file.corruptionError}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`badge me-2 ${file.type === VideoTypeEnum.PANORAMA ? 'bg-success' : 'bg-primary'}`}>
                                                                {file.type}
                                                            </span>
                                                            {file.isCorrupted && (
                                                                <span className="badge bg-danger me-2">
                                                                    CORRUPTED
                                                                </span>
                                                            )}
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => removeFile(index)}
                                                                title="Remove file"
                                                            >
                                                                <i className="bi bi-x"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                            {selectedVideoFiles.length > 5 && (
                                                <li className="list-group-item text-center">
                                                    ...and {selectedVideoFiles.length - 5} more
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                        {step === "review" && (
                            <RecordList records={recordGroups} onReuploadChange={setGroupReupload} />
                        )}
                        {step === "upload" && (
                            hasVideosToUpload ? (
                                <div className="upload-queue">
                                    <p>Uploading videos to records...</p>
                                    {uploadQueue.map((item: UploadItem, index: number) => (
                                        <div key={index} className="upload-item mb-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <div>
                                                    {item.file.name}
                                                    <span className="badge ms-2">
                                                        {item.type}
                                                    </span>
                                                </div>
                                                <div className="upload-status">
                                                    {item.status === "pending"
                                                        ? "Pending"
                                                        : item.status === "uploading"
                                                            ? "Uploading..."
                                                            : item.status === "completed"
                                                                ? "Completed"
                                                                : `Error: ${item.error}`}
                                                </div>
                                            </div>
                                            <div className="progress mb-1">
                                                <div
                                                    className={`progress-bar ${item.status === "error" ? "bg-danger" : ""}`}
                                                    role="progressbar"
                                                    style={{ width: `${item.progress}%` }}
                                                >
                                                    {item.progress}%
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    No video will be uploaded.
                                </div>
                            )
                        )}
                    </div>
                    <div className="modal-footer">
                        {step !== "select" && (
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleBack}
                                disabled={isCreatingRecords || isUploading}
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleNext}
                            disabled={(step === "select" && !batchName.trim()) || isCreatingRecords}
                        >
                            {step === "select" ? "Next" :
                                step === "review" ? (isCreatingRecords ? "Creating Records..." : "Create Records & Upload") :
                                    (isUploading ? "Uploading..." : "Close")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
